import { useState, useEffect, useCallback, useRef } from 'react';
import { getAuthHeaders, handleAuthError, isAdminLoggedIn, getAdminUser } from '../utils/authUtils';
import { getApiUrl } from '../../config/environment';
import { dispatchAdminAccessDeniedIfNeeded } from '../utils/adminApiError';

const useAnalytics = () => {
  /** After first successful load, refetches skip loading=true to avoid full-view “refresh” flashes */
  const hasLoadedOnceRef = useRef(false);
  const [data, setData] = useState({
    totalProducts: 0,
    totalUsers: 0,
    totalOrders: 0,
    totalRevenue: 0,
    recentOrders: [],
    lowStockProducts: [],
    statusCounts: {},
    todayOrders: 0,
    monthOrders: 0,
    todayRevenue: 0,
    monthRevenue: 0,
    totalCategories: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    // Only fetch if user is authenticated
    if (!isAdminLoggedIn()) {
      console.log('🔒 User not authenticated, skipping analytics fetch');
      return;
    }

    const adminUser = getAdminUser();
    if (adminUser?.role === 'team') {
      return;
    }

    try {
      const silent = hasLoadedOnceRef.current;
      if (!silent) setLoading(true);
      setError(null);

      const headers = getAuthHeaders();

      console.log('📊 Fetching analytics data...');

      const response = await fetch(getApiUrl('/admin/analytics'), {
        method: 'GET',
        headers
      });

      const result = await response.json().catch(() => ({}));

      if (response.status === 403) {
        dispatchAdminAccessDeniedIfNeeded(result, result.error || 'Access denied');
        setError(result.error || 'Access denied');
        return;
      }

      if (!response.ok) {
        // Handle 401 errors specifically
        if (response.status === 401) {
          console.error('❌ Unauthorized - Admin token may be invalid');
          throw new Error('Unauthorized - Please login again');
        }
        handleAuthError(new Error(`HTTP ${response.status}`), response);
      }
      
      console.log('📊 Analytics API Response:', result);
      
      if (result.success) {
        console.log('✅ Analytics data loaded:', result.data);
        setData(result.data);
        hasLoadedOnceRef.current = true;
      } else {
        console.log('❌ Analytics API Error:', result.message);
        throw new Error(result.message || 'Failed to fetch analytics');
      }
    } catch (error) {
      console.error('❌ Analytics error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch analytics when component mounts and user is authenticated
    if (isAdminLoggedIn()) {
      fetchAnalytics();
    }
  }, [fetchAnalytics]);

  return { data, loading, error, refetch: fetchAnalytics };
};

export default useAnalytics;
